import React, { useContext, useMemo } from 'react';
import { Size, ProductSizes } from 'src/components/ProductDetail/ProductSizes';
import { Swatch } from 'src/components/ProductDetail/ProductSwatch';
import { SwatchTitle } from 'src/components/ProductDetail/SwatchTitle';
import { appendStockToSize } from 'src/components/ProductDetail/utils/selectVariantsFromOptions';

import { BundleOptionsContext } from './BundleOptionsProvider';
import { BundleSectionTitle } from './BundleSectionTitle';
import { useFormDataCausesRender } from './BundleSelectionProvider';
import { FormItem } from './utils/formDataToPayload';
import classes from './ProductBundle.scss';

type Props = {
    sizes: Size[];
    swatches: Swatch[];
    initialValues?: Record<string, any>;
    sizeGuide?: string;
};

const TITLE = '2. Sizes & Quantities';

export const BundleSizeList: React.FC<Props> = React.memo((props) => {
    const { items } = useFormDataCausesRender();

    if (items.length === 0) {
        return <BundleSectionTitle title={TITLE} disabled={true} />;
    }

    return (
        <div className={classes.bundleSizeList}>
            <BundleSectionTitle title={TITLE} disabled={false} />
            {items.map((formItem) => {
                const items = props.initialValues && props.initialValues[`index:${formItem.swatch.value_index}`];
                return (
                    <BundleSize
                        key={formItem.swatch.value_index}
                        sizes={props.sizes}
                        sizeGuide={props.sizeGuide}
                        formItem={formItem}
                        quantities={items}
                    />
                );
            })}
        </div>
    );
});

type BundleSize = {
    sizes: Size[];
    formItem: FormItem;
    quantities?: Record<string, string>;
    sizeGuide?: string;
};

const BundleSize: React.FC<BundleSize> = React.memo((props) => {
    const { formItem, sizes, sizeGuide } = props;
    const { variants } = useContext(BundleOptionsContext);

    const computedSizes: Size[] = useMemo(() => {
        const output: Size[] = [];
        sizes.forEach((size) => {
            const fake_selection = {
                color: formItem.swatch.value_index,
            };
            const next = appendStockToSize(size, fake_selection, variants);
            /**
             * If the `simpleProductId` is NOT -1, that means that
             * stock information was obtained and this size should be shown
             */
            if (next.simpleProductId !== -1) {
                output.push(next);
            }
        });
        return output;
    }, [formItem.swatch.value_index, sizes, variants]);
    return (
        <ProductSizes
            key={formItem.swatch.value_index}
            title={<SwatchTitle swatch={formItem.swatch} />}
            sizes={computedSizes}
            quantities={props.quantities}
            sizeGuide={sizeGuide || ''}
            isBundle={true}
            fieldPrefix={`index:${formItem.swatch.value_index}.size`}
        />
    );
});
